/**
 * 作品线注册表 —— `/works` 四线聚合的**唯一接线处**。
 *
 * ★放 pages 层不放 entities:它要 import 三个 feature 的 service,而 FSD 单向核心是
 * features 之上才能 import features(entities 在 features 之下,反向 import 是层级倒挂)。
 * 纯类型在 `@/entities/work/model/types`,这里只做「DTO → WorkItem」的投影与服务适配。
 *
 * ★加一条线要改的地方就这一处:注册表加一行(mapper + service 适配),页面层零改动。
 * 官媒舱(`jizhe`)已于 R2-T1 接线(轻列表适配,见该条目内注释)。
 */
import { digitalHumanService } from "@/features/digital-human/api/digitalHumanService";
import { posterService, type PosterTaskDto } from "@/features/poster/api/posterService";
import { retryPosterTask } from "@/features/poster/model/retryQuote";
import {
  draftShellUseCase,
  isCanvasDraftRaw,
  isOneClickShellRaw,
} from "@/features/poster/longimage/model/canvasBridge";
import type { LongImagePlanJob } from "@/features/poster/longimage/model/canvas";
import { reporterService } from "@/features/reporter/api/reporterService";
import { videoService, type VideoDraftDto } from "@/features/video/api/videoService";
import { draftInFlight, draftStatusHint } from "@/features/video/model/draftStatusHint";
import { planInFlight, planStatusHint } from "@/features/poster/longimage/model/planStatusHint";
import type { OrgScopeParams } from "@/shared/hooks/useOrgScopeFilter";
import type { WorkDraft, WorkItem, WorkLineKey } from "@/entities/work/model/types";
import { DEPT_TITLE } from "@/entities/work/model/deptTitles";
import {
  mapDh,
  mapPoster,
  mapPosterOneClickJob,
  mapReporter,
  mapVideo,
  type RawPosterOneClickJob,
} from "@/entities/work/model/mappers";
import type { SceneHub } from "@/entities/scene-card/model/types";
import { LONG_IMAGE_USAGES } from "@/entities/scene-card/model/presets";
import { cardOfSceneKey } from "@/entities/scene-card/catalog";
import { parseLongImageName } from "@/entities/work/model/longImageName";

/** 长图稿壳里的用途(白名单外/读不到 → null)。 */
function longImageUsageOf(formJson: string): string | null {
  const u = draftShellUseCase(formJson);
  return u && (LONG_IMAGE_USAGES as readonly string[]).includes(u) ? u : null;
}

/** 长图稿壳里的用途 → `&usage=xx`(白名单外/读不到 → 空串,搭建器按直进渲染)。 */
export function longImageUsageQuery(formJson: string): string {
  const u = longImageUsageOf(formJson);
  return u ? `&usage=${u}` : "";
}

const DRAFT_NAME_PREFIX = "详情长图·";

/**
 * 长图稿草稿卡显示名(1006 复测:企业介绍稿在草稿区叫「详情长图·…」)。
 *
 * 草稿名在建稿时统一起成「详情长图·{主题}·{时间}」—— 这串**不改**:api 一键建单按它起作品名、
 * 视频标题从它剥线别前缀(只认「详情长图·/一键长图·」)。只在显示层按壳里的用途换成该用途的
 * 卡名(「企业介绍长图·…」),用途与 `longImageUsageQuery` 同一份白名单;读不到用途 / 电商详情原样。
 */
export function longImageDraftDisplayName(name: string, formJson: string): string {
  if (!name.startsWith(DRAFT_NAME_PREFIX)) return name;
  // 1006:新草稿名自带用途段(「详情长图·企业介绍长图·…」)→ 直接去掉线别前缀,不再叠一层用途名
  const parsed = parseLongImageName(name);
  if (parsed?.usage) return name.slice(DRAFT_NAME_PREFIX.length);
  const u = longImageUsageOf(formJson);
  if (!u || u === "goods") return name;
  const title = cardOfSceneKey(`longimage_${u}`)?.title;
  return title ? `${title}·${name.slice(DRAFT_NAME_PREFIX.length)}` : name;
}

export interface WorkListParams extends OrgScopeParams {
  page: number;
  size: number;
  /** 终态筛(ALL 由调用方转成 undefined);三线共用 media_task.status 词汇。 */
  status?: string;
  sceneKey?: string;
  q?: string;
  /** 1102 只看收藏:true 才下推;只有带 setFavorite 的线支持(新闻线没有收藏)。 */
  favorite?: boolean;
}

export interface WorkPage {
  items: WorkItem[];
  total: number;
}

export interface WorkLineDef {
  key: WorkLineKey;
  /** 模块权限门(useModuleVisible);无权限 = 该线整条静默消失,不报错。 */
  moduleKey: string;
  /** 部门名(tab 名 / 卡面归属名)。 */
  label: string;
  /** 场景筛 chips 的取数 hub;null = 该线无场景卡。 */
  hub: SceneHub | null;
  /** hub 页路由(空态里「去创作」按钮用)。 */
  hubRoute: string;
  /**
   * 是否已接线。false = 留位(不发请求、不出 tab)。
   * ★不做「即将上线」假 tab:点了会是永远空的列表,那是承诺了不兑现。
   */
  wired: boolean;
  listHistory(p: WorkListParams): Promise<WorkPage>;
  listRunning(p: OrgScopeParams): Promise<WorkItem[]>;
  /** 该线无草稿概念(分身)则不实现。 */
  listDrafts?(): Promise<WorkDraft[]>;
  deleteDraft?(draftId: string): Promise<unknown>;
  /** ★与 retry 同口径:后端没有的动作就不实现 → 卡上不出按钮(官媒线无改名/收藏/删除端点)。 */
  rename?(id: number, name: string): Promise<unknown>;
  setFavorite?(id: number, v: boolean): Promise<unknown>;
  remove?(id: number): Promise<unknown>;
  /** 后端无 retry 端点的线(新闻)不实现 → 卡上不出重试按钮。 */
  retry?(id: number): Promise<unknown>;
  /** 重试确认框的说明(按该线 retry 接口的实际语义写);不填用通用「会消耗积分。」。 */
  retryConfirmDescription?: string;
}

/**
 * 分身重试确认文案 —— 按 api `DigitalHumanServiceImpl.retry` 实际语义:复原原参数走 doCreate 新建任务
 * (新任务重新问价扣积分,原任务不动);直传音频 key 不复用(改文案配音)、BGM 不复配。
 */
export const DH_RETRY_CONFIRM_DESCRIPTION =
  "会按原形象、文案和音色新建一条口播视频,按新任务重新扣积分;原作品保留。原片若用上传音频驱动,重试改用文案配音,背景音乐不沿用。";

/* ────────────────────────── 注册表 ────────────────────────── */

export const WORK_LINES: WorkLineDef[] = [
  {
    key: "poster",
    moduleKey: "ai_poster",
    label: DEPT_TITLE.poster,
    hub: "poster",
    hubRoute: "/ai-haibao",
    wired: true,
    listHistory: async (p) => {
      const r = await posterService.listTasks({
        bucket: "history",
        page: p.page,
        size: p.size,
        status: p.status as PosterTaskDto["status"] | undefined,
        sceneKey: p.sceneKey,
        q: p.q,
        favorite: p.favorite || undefined,
        scope: p.scope,
        departmentId: p.departmentId,
        memberId: p.memberId,
      });
      return { items: (r.records ?? []).map(mapPoster), total: r.total ?? 0 };
    },
    listRunning: async (s) => {
      const [r, oneClickJobs] = await Promise.all([
        posterService.listTasks({ bucket: "running", page: 0, size: 20, ...s }),
        // ★一键长图「点完即走」(2026-09-04):读懂+排区块+派发这一段还没有 xb_media_task 行
        //   (AFTER_COMMIT 才建),`listTasks` 那条门看不见它 —— 不补这一份,提交后切到作品页
        //   这段窗口那单彻底不存在。owner-scoped(军师内部门那条见 posterService 头注,互不影响),
        //   ★不吃 `s`(部门/成员筛选):这扇门目前只查「我自己」的作业,管理层切「全部」时看不到
        //   下属这段窗口的在途单 —— 诚实的缺口(见 posterService.listLongImageOneClickJobs 头注),
        //   等真任务落地后(几秒到一两分钟内)会经 `listTasks` 正常纳入部门视野,不是永久看不见。
        // ★catch 空数组必须与成功分支同型:`() => []` 推成 never[],联合数组上
        //   filter 的类型谓词重载不生效,下面的收窄整个白写(TS2345 的另一半根)。
        posterService
          .listLongImageOneClickJobs()
          .catch((): Awaited<ReturnType<typeof posterService.listLongImageOneClickJobs>> => []),
      ]);
      // 屏卡 P1(0910):FAILED 也放出来(近 24h)——此前被整个滤掉,一键失败在作品库
      // 彻底不可见(用户关页即失联,质检闸 9 连杀全程无感)。只收近 24h 防陈年失败卡
      // 常驻;出了真任务的失败(posterTaskId 在)由任务卡自己说,不重复。
      const dayAgo = Date.now() - 24 * 3600_000;
      // ★类型谓词不是摆设:RawPosterOneClickJob.status 刻意不含 DONE(真任务卡覆盖,
      //   见 mappers.ts 头注),这里在类型层同步剔掉 —— 少了它 map 那行就是 TS2345
      //   (0908 起挂的预存量红,0910 修)。
      const visible = oneClickJobs.filter(
        (
          j
        ): j is LongImagePlanJob & {
          posterTaskId?: number | null;
          createdAt?: string | null;
          status: RawPosterOneClickJob["status"];
        } => {
          if (j.status === "QUEUED" || j.status === "RUNNING") return true;
          if (j.status !== "FAILED" || j.posterTaskId != null) return false;
          const at = j.createdAt ? Date.parse(j.createdAt) : NaN;
          return Number.isFinite(at) && at >= dayAgo;
        }
      );
      // ★用途回填(Z9d 0926):作业行没有 usage 列,而卡点回搭建器**必须带 usage**(否则落错路卡 /
      //   读错备料桶 / 价签变并集区间,见 mappers.mapPosterOneClickJob 头注)。创建时 ensureDraft 把
      //   presetUsage 写进了作业锚壳的 formJson.useCase —— 从草稿列表按 draftId 读回来,与创建时同源。
      //   只在有一键卡要画时才多读一次草稿门(空列表零开销);读不到(网络/壳已清)→ 不带 usage,
      //   卡照出,与旧行为一致。
      // ★1005:同一次草稿读顺带取锚壳草稿名 —— 在途卡标题用它(产品名+时间),不再纯编号。
      const usageByDraft = new Map<number, string>();
      const nameByDraft = new Map<number, string>();
      if (visible.some((j) => j.draftId != null)) {
        const drafts = await posterService.listDrafts().catch(() => []);
        for (const d of drafts ?? []) {
          const u = draftShellUseCase(String(d.formJson ?? ""));
          if (u) usageByDraft.set(d.draftId, u);
          if (d.name) nameByDraft.set(d.draftId, d.name);
        }
      }
      return [
        ...(r.records ?? []).map(mapPoster),
        ...visible.map((j) =>
          mapPosterOneClickJob({
            ...j,
            usage: j.draftId != null ? (usageByDraft.get(j.draftId) ?? null) : null,
            draftName: j.draftId != null ? (nameByDraft.get(j.draftId) ?? null) : null,
          })
        ),
      ];
    },
    listDrafts: async () => {
      const list = await posterService.listDrafts();
      // ★一键作业锚壳不出草稿卡(0911 老板拍板):一键用户面只有「处理中」,进度/
      //   结果由处理中卡与任务卡接管;这张壳没有任何可编辑内容,出卡=纯噪音。
      //   编排壳(无 oneclick 标)照出——编排完成靠它回来接稿。
      return (list ?? [])
        .filter((d) => !isOneClickShellRaw(String(d.formJson ?? "")))
        .map((d) => ({
          uid: `poster-draft-${d.draftId}`,
          line: "poster" as const,
          lineLabel: DEPT_TITLE.poster,
          draftId: String(d.draftId),
          name: d.name
            ? longImageDraftDisplayName(d.name, String(d.formJson ?? ""))
            : `草稿 #${d.draftId}`,
          coverUrl: null,
          updatedAt: d.updatedAt ?? "",
          // ★海报草稿恢复的是 6 步向导(/ai-haibao/new),不是 /create —— 路由不同,照搬会 404。
          // ★★长图智能稿与向导共用 /poster/drafts 一个存储(useCanvasBuilder 云端自动保存),
          //   一律送向导 = 智能稿点「继续编辑」落进自由创作、formJson 读不懂,看起来像草稿丢了
          //   (2026-08-10 用户报障)。按 formJson 外壳 kind 标记分流:智能稿裸进搭建器,
          //   useCanvasBuilder 自己拉 listDrafts 恢复(「已恢复智能稿草稿·云端自动保存」那条路)。
          // ★带上 draftId(2026-09-08 跳转收敛):智能稿裸送 /detail-long 时,useCanvasBuilder
          //   只能「拉 listDrafts 取第一份能解析的」——出过 A 稿又做了 B 稿,从「我的作品」点 A
          //   打开的却是 B(用户报「打开的不是那一稿」)。带 draftId,恢复扫描把这一份排最前、
          //   开的就是这一份(与「去改那一块」同一条 openDraftId 通道)。
          // ★1006 复测:企业介绍稿编排后从草稿卡回来,标题变「详情长图」、画风「AI 定」——
          //   回搭建器只带了 draftId,presetUsage 为空 → 页头/备料桶/推荐画风全按直进渲染。
          //   用途从壳里读回来带上(与一键在途卡 mapPosterOneClickJob 同一份白名单)。
          resumeRoute: isCanvasDraftRaw(d.formJson)
            ? `/ai-haibao/detail-long?draftId=${d.draftId}${longImageUsageQuery(String(d.formJson ?? ""))}`
            : `/ai-haibao/new?draftId=${d.draftId}`,
          // ★1005:智能编排在途/完成/失败要在卡上说出来(列表本来就带 planStatus,只是没显示),
          //   在途时 busy → 作品页给卡挂「编排中」角标并轮询,编排完成卡上自动变「编排好了」。
          statusHint: planStatusHint(d.planStatus),
          busy: planInFlight(d.planStatus),
        }));
    },
    deleteDraft: (draftId) => posterService.deleteDraft(Number(draftId)),
    rename: (id, name) => posterService.updateTaskMeta(id, { name }),
    setFavorite: (id, v) => posterService.updateTaskMeta(id, { isFavorite: v }),
    remove: (id) => posterService.deleteTask(id),
    // 修复波 0926(代理 H):卡上没有 provider/档位,retryPosterTask 现拉详情再取单。
    retry: (id) => retryPosterTask(id),
  },
  {
    key: "video",
    moduleKey: "ai_video",
    label: DEPT_TITLE.video,
    hub: "video",
    hubRoute: "/ai-video",
    wired: true,
    listHistory: async (p) => {
      const r = await videoService.listTasks({
        bucket: "history",
        page: p.page,
        size: p.size,
        status: p.status,
        sceneKey: p.sceneKey,
        q: p.q,
        favorite: p.favorite || undefined,
        scope: p.scope,
        departmentId: p.departmentId,
        memberId: p.memberId,
      });
      return { items: (r.records ?? []).map(mapVideo), total: r.total ?? 0 };
    },
    listRunning: async (s) => {
      const r = await videoService.listTasks({ bucket: "running", page: 0, size: 20, ...s });
      return (r.records ?? []).map(mapVideo);
    },
    listDrafts: async () => {
      const list = await videoService.listDrafts();
      return (list ?? []).map((d: VideoDraftDto) => ({
        uid: `video-draft-${d.draftId}`,
        line: "video" as const,
        lineLabel: DEPT_TITLE.video,
        draftId: String(d.draftId),
        name: d.name || `草稿 #${d.draftId}`,
        coverUrl: d.coverThumbUrl ?? null,
        updatedAt: d.updatedAt ?? "",
        resumeRoute: `/ai-video/create?draftId=${d.draftId}`,
        // ★别再把 frameGenStatus 丢掉:它是「这份稿子正在编排」的唯一来源(见 draftStatusHint)。
        statusHint: draftStatusHint(d.frameGenStatus),
        busy: draftInFlight(d.frameGenStatus),
        producedRoute: d.consumedTaskId ? `/ai-video/tasks/${d.consumedTaskId}` : undefined,
      }));
    },
    deleteDraft: (draftId) => videoService.deleteDraft(Number(draftId)),
    rename: (id, name) => videoService.updateTaskMeta(id, { name }),
    setFavorite: (id, v) => videoService.updateTaskMeta(id, { isFavorite: v }),
    remove: (id) => videoService.deleteTask(id),
    retry: (id) => videoService.retryTask(id),
  },
  {
    key: "dh",
    moduleKey: "ai_digital_human",
    label: DEPT_TITLE.dh,
    hub: "dh",
    hubRoute: "/ai-digital-human",
    wired: true,
    listHistory: async (p) => {
      const r = await digitalHumanService.listTasks({
        bucket: "history",
        page: p.page,
        size: p.size,
        status: p.status,
        sceneKey: p.sceneKey,
        q: p.q,
        favorite: p.favorite || undefined,
        scope: p.scope,
        departmentId: p.departmentId,
        memberId: p.memberId,
      });
      return { items: (r.records ?? []).map(mapDh), total: r.total ?? 0 };
    },
    listRunning: async (s) => {
      const r = await digitalHumanService.listTasks({
        bucket: "running",
        page: 0,
        size: 20,
        ...s,
      });
      return (r.records ?? []).map(mapDh);
    },
    // 分身无草稿 → listDrafts 不实现。retry 1015 接上 api `POST /tasks/{id}/retry`(新建任务、重新计费)。
    rename: (id, name) => digitalHumanService.updateTaskMeta(id, { name }),
    setFavorite: (id, v) => digitalHumanService.updateTaskMeta(id, { isFavorite: v }),
    remove: (id) => digitalHumanService.deleteTask(id),
    retry: (id) => digitalHumanService.retryTask(id),
    retryConfirmDescription: DH_RETRY_CONFIRM_DESCRIPTION,
  },
  {
    // ★官媒舱(R2-T1 接线):moduleKey 统一为后端 catalog 的 `ai_reporter`(旧 `ai_jizhe` 是
    // console 单方面造的名,后端从来不认 —— 双轨则门控/作品线全失效)。
    //
    // 后端只有舱内轻列表(GET /drafts?limit,租户级、无分页/无 owner 归属/无改名收藏删除端点),
    // 所以:筛选在客户端做;org 归属筛(部门/成员)无从判定 → 返回空,**不冒充**「这些都是他做的」;
    // 改名/收藏/删除/重试不实现 → 卡上不出按钮(与分身线 retry 同口径,不做假交互)。
    key: "jizhe",
    moduleKey: "ai_reporter",
    label: DEPT_TITLE.jizhe,
    hub: "reporter",
    hubRoute: "/ai-jizhe",
    wired: true,
    listHistory: async (p) => {
      // 批三:owner 归属列已透出,「只看我的」走服务端 creator 过滤;部门/成员筛仍无
      // org 归属判定 → 返回空(不冒充「这些是他的」)。
      if (p.departmentId != null || p.memberId != null) {
        return { items: [], total: 0 };
      }
      // ★codex P2:按 p.size 取会让 total===items.length,「加载更多」永远不出现。
      // 一次取满客户端上限(200 > works 归并深度上限 192)再筛,total 才是真的;
      // 超过 200 条的深史走筛选(与 works 页「到顶提示用筛选」同口径)。
      const list = await reporterService.listDrafts(200, p.scope === "mine");
      const q = p.q?.trim().toLowerCase();
      const items = (list ?? [])
        .filter((d) => d.status === "GENERATED" || d.status === "EDITED" || d.status === "FAILED")
        .map(mapReporter)
        .filter((w) => (p.status ? w.status === p.status : true))
        .filter((w) => (p.sceneKey ? w.sceneKey === p.sceneKey : true))
        .filter((w) => (q ? w.title.toLowerCase().includes(q) : true));
      // 切片要吃 p.page 偏移:单线视图是真翻页(page>0);「全部」视图恒 page=0、靠 size 增长,行为不变。
      return { items: items.slice(p.page * p.size, (p.page + 1) * p.size), total: items.length };
    },
    listRunning: async (s) => {
      if (s.departmentId != null || s.memberId != null) return [];
      const list = await reporterService.listDrafts(20, s.scope === "mine");
      return (list ?? []).filter((d) => d.status === "GENERATING").map(mapReporter);
    },
    // 批三:删除接后端软删端点(生成中后端拒删,错误如实弹出);改名/收藏仍无端点不做假交互。
    remove: (id) => reporterService.deleteDraft(id),
  },
];

export const WIRED_LINES = WORK_LINES.filter((l) => l.wired);

export function lineOf(key: WorkLineKey): WorkLineDef {
  const l = WORK_LINES.find((x) => x.key === key);
  if (!l) throw new Error(`unknown work line: ${key}`);
  return l;
}

export { mergeWorks } from "@/entities/work/model/mappers";
